/**
 * Reset password — pre-shell page at `/reset-password`. Ports the Flutter
 * `reset_password_page.dart`: reads the recovery token from the URL search
 * params (`?token=` / `?code=`), takes a new password (live NIST checklist)
 * + confirm, calls `useAuth().resetPassword`, then shows a success state that
 * links to `/login`. A missing token surfaces an error and a link back to
 * "forgot password". Token-driven; no raw hex.
 */
import { useCallback, useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { CheckCircle2 } from 'lucide-react'
import { useAuth } from '@/app/auth-context'
import { Button } from '@/components/ui'
import { AuthErrorAlert, AuthPaneHeader, AuthShell } from './auth-shell'
import { PasswordInput } from './password-input'
import { PasswordChecklist } from './password-checklist'
import { validateStrongPassword } from './password-rules'

export const ResetPasswordPage = function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as Record<string, unknown>
  const token =
    typeof search.token === 'string'
      ? search.token
      : typeof search.code === 'string'
        ? search.code
        : ''

  return (
    <AuthShell>
      {token ? <ResetForm token={token} /> : <MissingTokenView />}
    </AuthShell>
  )
}

function ResetForm({ token }: { token: string }) {
  const { resetPassword } = useAuth()
  const navigate = useNavigate()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [touched, setTouched] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const passwordError = touched ? validateStrongPassword(password) : undefined
  const confirmError = touched && confirm !== password ? 'Passwords do not match' : undefined

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (submitting) return
      setTouched(true)
      if (validateStrongPassword(password) || confirm !== password) return
      setFormError(null)
      setSubmitting(true)
      resetPassword(token, password)
        .then(() => setDone(true))
        .catch((err: unknown) => {
          setFormError(err instanceof Error ? err.message : 'Could not reset your password')
        })
        .finally(() => setSubmitting(false))
    },
    [submitting, password, confirm, token, resetPassword],
  )

  if (done) {
    return <SuccessView onContinue={() => void navigate({ to: '/login' })} />
  }

  return (
    <>
      <AuthPaneHeader title="Reset password" subtitle="Enter your new password below." />
      <form
        onSubmit={onSubmit}
        noValidate
        style={{ marginTop: 'var(--s-6)', display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}
      >
        <PasswordInput
          label="New password"
          autoComplete="new-password"
          value={password}
          error={passwordError}
          onChange={(e) => setPassword(e.currentTarget.value)}
          disabled={submitting}
        />
        <PasswordChecklist password={password} />
        <PasswordInput
          label="Confirm new password"
          autoComplete="new-password"
          value={confirm}
          error={confirmError}
          onChange={(e) => setConfirm(e.currentTarget.value)}
          disabled={submitting}
        />
        {formError && <AuthErrorAlert message={formError} />}
        <Button type="submit" size="lg" loading={submitting} disabled={submitting}>
          {submitting ? 'Resetting…' : 'Reset password'}
        </Button>
      </form>
    </>
  )
}

function SuccessView({ onContinue }: { onContinue: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 44,
          height: 44,
          borderRadius: 'var(--r-pill)',
          background: 'var(--ok-100)',
          color: 'var(--ok-700)',
        }}
      >
        <CheckCircle2 size={22} strokeWidth={1.75} />
      </span>
      <AuthPaneHeader
        title="Password updated"
        subtitle="Your password has been reset. Sign in with your new password to continue."
      />
      <Button size="lg" onClick={onContinue}>
        Back to sign in
      </Button>
    </div>
  )
}

function MissingTokenView() {
  return (
    <>
      <AuthPaneHeader
        title="Reset password"
        subtitle="This password-reset link is invalid or has expired."
      />
      <div style={{ marginTop: 'var(--s-6)', display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
        <AuthErrorAlert message="Invalid or missing recovery link. Please request a new password reset." />
        <Link to="/forgot-password">
          <Button size="lg" variant="outline" style={{ width: '100%' }}>
            Request a new link
          </Button>
        </Link>
      </div>
    </>
  )
}
